import remote from "@/data/remote";
import { getId, fetchOnce } from "@/utils/commonHelper.js";

export function getMeasurements(stock = {}, measurement = {}, unitOptions = []) {
	const unitCode = measurement.unit ?? stock.unit;
	if (unitCode && Array.isArray(unitOptions)) {
		const unitDef = unitOptions.find((u) => u.code === unitCode);
		if (unitDef && Array.isArray(unitDef.entryFormats)) {
			for (const fmt of unitDef.entryFormats) {
				const req = fmt.requiredMeasurements || [];
				const allPresent = req.every((k) => (measurement[k] ?? stock[k]) != null);
				if (allPresent) {
					if (req.length === 0) {
						const val = measurement.value ?? stock.value;
						return val != null ? [{ key: "value", value: val }] : [];
					}
					return req.map((k) => ({ key: k, value: measurement[k] ?? stock[k] }));
				}
			}
		}
	}
	return ["length", "width", "height", "value", "pieces"]
		.filter((k) => (measurement[k] ?? stock[k]) != null)
		.map((k) => ({ key: k, value: measurement[k] ?? stock[k] }));
}

export function formatDimensions(dimensions) {
	return dimensions.map((d) => d.value).join("×");
}

const stockCache = new Map();
const prodCache = new Map();

async function fetchAttrDefs(productId) {
	try {
		const items = await new Promise((res) =>
			remote.productAttributes.getByProduct(productId, {
				onSuccess: (r) => res(r.items || []),
				onFail: () => res([]),
			})
		);
		return items;
	} catch {
		return [];
	}
}

export async function loadStockBundle(raw) {
	const stock =
		typeof raw === "object"
			? raw
			: await fetchOnce(
					stockCache,
					raw,
					(id) => new Promise((res, rej) => remote.stocks.find(id, { onSuccess: res, onFail: rej }))
			  );
	if (!stock) return null;

	const productId = getId(stock.product?.productId ?? stock.productId);
	const product = await fetchOnce(
		prodCache,
		productId,
		(id) => new Promise((res, rej) => remote.products.find(id, { onSuccess: res, onFail: rej }))
	);

	const variant =
		(product.variants || []).find(
			(v) => String(getId(v._id)) === String(getId(stock.product?.variantId ?? stock.variantId))
		) || {};

	const measurement =
		(variant.measurements || []).find(
			(m) => String(getId(m._id)) === String(getId(stock.product?.measurementId ?? stock.measurementId))
		) || {};

	const attrDefs = await fetchAttrDefs(productId);

	return {
		stock,
		product,
		variant,
		measurement,
		attrDefs,
	};
}

export function formatProductName(
	bundle,
	lang = "en",
	{ showCode = true, showName = true, showDimensions = true, showUnit = true, separator = " - " } = {}
) {
	if (!bundle) return "";
	const { stock, variant, measurement, product } = bundle;
	const parts = [];

	if (showCode && variant.code) parts.push(variant.code);

	const name = (variant.name?.[lang] || variant.name?._default || "").trim();
	if (showName && name) parts.push(name);

	if (showDimensions) {
		const dims = getMeasurements({ unit: stock.product?.unit || stock.unit }, measurement, product.units);
		if (dims.length) {
			const dimTxt = formatDimensions(dims);
			const unitTxt = showUnit ? measurement.unit || stock.product?.unit || stock.unit || "" : "";
			parts.push(`${dimTxt}${unitTxt}`);
		}
	}

	return parts.join(separator);
}

export function getWarehouseName({ $store, stockData }) {
	const warehouses = $store?.getters?.company?.warehouses || [];
	const w = stockData?.warehouse;
	if (!w) return "";
	const id = String(getId(w));
	return (warehouses.find((x) => String(getId(x)) === id) || {}).name || "";
}

export function getProductUnit(bundle) {
	if (!bundle) return "";
	const { product, measurement, stock } = bundle;
	const units = product?.units || [];
	let symbol = measurement.format?.split(" (")[0].trim() || measurement.unit || stock.product?.unit || stock.unit || "";
	return (units.find((u) => u.code === symbol) || {}).code || symbol;
}

export function getUnitName(vm, unitCode, unitOptions) {
	const found = (unitOptions || []).find((u) => u.code === unitCode);
	if (!found) return unitCode || "";
	return vm.$t?.(`$unit.${found.name.toLowerCase()}`) ?? found.name;
}

export function getAttributes(vm, stockAttrs = [], attributeDefs = []) {
	const lang = vm?.$lang || "en";
	return (stockAttrs || [])
		.map((av) => {
			const def = attributeDefs.find((d) => String(getId(d)) === String(getId(av.attributeId)));
			if (!def) return null;
			let val = "";
			switch (def.type) {
				case "boolean":
					if (av.value) val = def.name?.[lang] || def.name?._default || "";
					break;
				case "multi_select":
					const ids = Array.isArray(av.value) ? av.value : [av.value];
					val = ids
						.map((id) => {
							const opt = def.options.find((o) => String(getId(o._id)) === String(id));
							return opt ? opt.value[lang] || opt.value._default : "";
						})
						.filter(Boolean)
						.join(", ");
					break;
				case "date":
					val = av.value ? new Date(av.value).toLocaleDateString() : "";
					break;
				default:
					val = av.value;
			}
			return val;
		})
		.filter(Boolean)
		.join("\n");
}
